import type { errorMessages } from '@/lang/nl/error';
import type { infoMessages } from '@/lang/nl/info';
import { successMessages } from '@/lang/nl/success';
import type { warningMessages } from '@/lang/nl/warning';
import i18n from '@/plugins/i18n';
import type { NamedValue } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';

const toast = useToast();
const shownToasts: string[] = [];

type ToastType = 'success' | 'error' | 'info' | 'warning';

function showToast(type: ToastType, message: string, variables: NamedValue = {}): void {
    if (shownToasts.includes(message)) return;

    shownToasts.push(message);
    if (type === 'success') toast.success(message, variables);
    if (type === 'error') toast.error(message, variables);
    if (type === 'info') toast.info(message, variables);
    if (type === 'warning') toast.warning(message, variables);

    setTimeout(() => removeToastMessageFromArray(message), 3000);
}

function removeToastMessageFromArray(message: string): void {
    const index = shownToasts.indexOf(message);
    if (index !== -1) {
        shownToasts.splice(index, 1);
    }
}

/**
 * Rather use the default toast methods, but sometimes you need to show a toast without translation.
 */
export function showUntranslatedToast(type: ToastType, message: string, options: NamedValue = {}): void {
    showToast(type, message, options);
}

export default {
    success: (key: keyof typeof successMessages, options: NamedValue = {}) => showToast('success', i18n.global.t(`success.${key}`, options)),

    error: (key: keyof typeof errorMessages, options: NamedValue = {}) =>
        showToast('error', i18n.global.t(`error.${key}`, options as Record<string, unknown>)),

    info: (key: keyof typeof infoMessages, options: NamedValue = {}) =>
        showToast('info', i18n.global.t(`info.${key}`, options as Record<string, unknown>)),

    warning: (key: keyof typeof warningMessages, options: NamedValue = {}) =>
        showToast('warning', i18n.global.t(`warning.${key}`, options as Record<string, unknown>))
};
